/**
 * HealthFacilityDetail — /health/facilities/:slug
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Building2, MapPin, Phone, Mail, Globe, Clock, ShieldAlert,
  Stethoscope, Users, CalendarPlus, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";

export default function HealthFacilityDetail() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/health/facilities/${slug}`)
      .then(r => setDoc(r.data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <><HealthCategoryNav /><div className="max-w-4xl mx-auto p-8 opacity-60 inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div></>;
  if (!doc) return <><HealthCategoryNav /><div className="max-w-4xl mx-auto p-8">Not found.</div></>;

  return (
    <>
      <HealthCategoryNav />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="facility-detail">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-2">
          {doc.facility_type}{doc.category ? ` · ${doc.category}` : ""}
        </div>
        <h1 className="font-display text-4xl mb-2">{doc.name}</h1>
        {doc.emergency && (
          <div className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full mb-3"
               style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid="badge-emergency">
            <ShieldAlert className="w-4 h-4"/>24/7 Emergency Services
          </div>
        )}

        {doc.description && <p className="mb-6 opacity-90 whitespace-pre-line">{doc.description}</p>}

        <div className="grid gap-4 md:grid-cols-2 mb-6">
          {(doc.address || doc.city || doc.country) && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>Address</div>
              <div className="text-sm">{doc.address || [doc.city, doc.state, doc.country].filter(Boolean).join(", ")}</div>
            </div>
          )}
          {doc.contact_phone && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Phone className="w-3 h-3"/>Phone</div>
              <a href={`tel:${doc.contact_phone}`} className="text-sm wofy-link">{doc.contact_phone}</a>
            </div>
          )}
          {doc.contact_email && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Mail className="w-3 h-3"/>Email</div>
              <a href={`mailto:${doc.contact_email}`} className="text-sm wofy-link">{doc.contact_email}</a>
            </div>
          )}
          {doc.website && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Globe className="w-3 h-3"/>Website</div>
              <a href={doc.website} target="_blank" rel="noopener noreferrer" className="text-sm wofy-link truncate block">{doc.website}</a>
            </div>
          )}
          {(doc.hours || doc.visiting_hours) && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>Hours</div>
              <div className="text-sm whitespace-pre-line">{doc.hours || doc.visiting_hours}</div>
            </div>
          )}
        </div>

        {doc.services?.length > 0 && (
          <div className="mb-6">
            <h2 className="font-display text-xl mb-2 inline-flex items-center gap-1.5"><Stethoscope className="w-4 h-4"/>Services</h2>
            <div className="flex flex-wrap gap-1.5">
              {doc.services.map((s, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {doc.departments?.length > 0 && (
          <div className="mb-6">
            <h2 className="font-display text-xl mb-2 inline-flex items-center gap-1.5"><Users className="w-4 h-4"/>Departments</h2>
            <div className="flex flex-wrap gap-1.5">
              {doc.departments.map((d, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{d}</span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8">
          <Link to={`/health/appointments/new?facility_slug=${encodeURIComponent(doc.slug)}`}
            className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="request-appointment">
            <CalendarPlus className="w-4 h-4"/>Request an appointment
          </Link>
        </div>
      </div>
    </>
  );
}
